import { createContext, useContext, ReactNode } from 'react';

interface ApiContextType {
  baseUrl: string;
  get: (path: string) => Promise<any>;
  post: (path: string, data?: any) => Promise<any>;
}

const ApiContext = createContext<ApiContextType>({
  baseUrl: '',
  get: async () => {
    throw new Error('ApiContext not initialized');
  },
  post: async () => {
    throw new Error('ApiContext not initialized');
  },
});

export const useApi = () => useContext(ApiContext);

interface ApiProviderProps {
  children: ReactNode;
  baseUrl: string;
}

export const ApiProvider: React.FC<ApiProviderProps> = ({ children, baseUrl }) => {
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const clearSessionAndNotify = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    window.dispatchEvent(new CustomEvent('saga-auth-expired'));
  };

  const get = async (path: string) => {
    const hadToken = !!localStorage.getItem('access_token');
    const response = await fetch(`${baseUrl}${path}`, {
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (response.status === 401 && hadToken) {
      clearSessionAndNotify();
    }
    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }
    return response.json();
  };

  const post = async (path: string, data?: any) => {
    const hadToken = !!localStorage.getItem('access_token');
    const response = await fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    });
    if (response.status === 401 && hadToken) {
      clearSessionAndNotify();
    }
    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }
    return response.json();
  };

  return (
    <ApiContext.Provider value={{ baseUrl, get, post }}>
      {children}
    </ApiContext.Provider>
  );
}; 